import { useEffect, useState } from "react";
import { Users, UserPlus, TrendingUp, UserX, CalendarCheck, Search } from "lucide-react";
import {
  PieChart, Pie, Cell, Legend, Tooltip,
  BarChart, Bar, XAxis, YAxis, ResponsiveContainer, LabelList,
} from "recharts";
import CRM_API from "../../api/crmApi";
import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "../../components/ui/table";

const STAT_CARDS = [
  { key: "total_leads",     label: "Total Leads",      icon: Users,         color: "bg-blue-500",   text: "text-blue-600",   filter: null },
  { key: "new_leads",       label: "New Leads",        icon: UserPlus,      color: "bg-cyan-500",   text: "text-cyan-600",   filter: "New Leads" },
  { key: "converted",       label: "Converted",        icon: TrendingUp,    color: "bg-green-500",  text: "text-green-600",  filter: "Converted" },
  { key: "dropped",         label: "Dropped",          icon: UserX,         color: "bg-red-500",    text: "text-red-600",    filter: "Dropped" },
  { key: "today_followups", label: "Today Followups",  icon: CalendarCheck, color: "bg-violet-500", text: "text-violet-600", filter: "Today Followups" },
];

const TEMP_FILTERS = [
  { key: "Hot Leads",  emoji: "🔥", label: "Hot Leads",  badge: "hot" },
  { key: "Warm Leads", emoji: "🟡", label: "Warm Leads", badge: "warm" },
  { key: "Cold Leads", emoji: "🔵", label: "Cold Leads", badge: "cold" },
];

const TEMP_COLORS = { hot: "#22c55e", warm: "#eab308", cold: "#ef4444" };
const SOURCE_COLORS = ["#22c55e","#3b82f6","#f59e0b","#ef4444","#8b5cf6","#06b6d4","#f97316","#14b8a6"];

export default function CrmDashboard({ onNavigate }) {
  const [stats, setStats] = useState({ total_leads: 0, new_leads: 0, converted: 0, dropped: 0, today_followups: 0 });
  const [leads, setLeads] = useState([]);
  const [title, setTitle] = useState("");
  const [search, setSearch] = useState("");
  const [activeTemp, setActiveTemp] = useState(null);

  // analytics
  const [followupMetrics, setFollowupMetrics] = useState(null);
  const [employeeMetrics, setEmployeeMetrics] = useState([]);
  const [temperatureData, setTemperatureData] = useState([]);
  const [courseData, setCourseData] = useState([]);
  const [sourceData, setSourceData] = useState([]);

  const isAdmin = localStorage.getItem("crm_role") === "admin";

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchStats();
    fetchLeads();
    if (isAdmin) fetchAnalytics();
  }, []);

  const fetchStats = async () => {
    try {
      const res = await CRM_API.get("/dashboard/stats");
      setStats(res.data);
    } catch (error) { console.error("Dashboard API error", error); }
  };

  const fetchAnalytics = async () => {
    try {
      const [emp, follow, temp, course, source] = await Promise.all([
        CRM_API.get("/dashboard/employee-performance").catch(() => ({ data: [] })),
        CRM_API.get("/dashboard/followup-metrics").catch(() => ({ data: {} })),
        CRM_API.get("/dashboard/temperature").catch(() => ({ data: [] })),
        CRM_API.get("/dashboard/course").catch(() => ({ data: [] })),
        CRM_API.get("/dashboard/source-count").catch(() => ({ data: [] })),
      ]);
      setEmployeeMetrics(emp.data || []);
      setFollowupMetrics(follow.data || null);
      setTemperatureData(temp.data || []);
      setCourseData(course.data || []);
      setSourceData(source.data || []);
    } catch (err) {
      console.log("Analytics fetch error", err);
    }
  };

  const fetchLeads = async (params = {}) => {
    try {
      const res = await CRM_API.get("/leads", { params });
      setLeads(res.data);
      setTitle("All Leads");
    } catch (error) { console.log("Error loading leads", error); }
  };

  const loadLeads = async (type) => {
    let params = {};
    if (type === "New Leads") params.status = "new";
    if (type === "Converted") params.status = "converted";
    if (type === "Dropped") params.status = "dropped";
    if (type === "Hot Leads") params.temperature = "hot";
    if (type === "Warm Leads") params.temperature = "warm";
    if (type === "Cold Leads") params.temperature = "cold";
    if (type === "Today Followups") params.today_followups = true;
    try {
      const res = await CRM_API.get("/leads", { params });
      setLeads(res.data);
      setTitle(type);
    } catch (error) { console.log("Filter error", error); }
  };

  const filteredLeads = leads.filter((l) =>
    l.student_name?.toLowerCase().includes(search.toLowerCase()) ||
    l.student_contact?.includes(search) ||
    l.interested_course?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-6">

      {/* ── 1. Stat Cards ───────────────────────────────── */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
        {STAT_CARDS.map(({ key, label, icon: Icon, color, text, filter }) => (
          <Card
            key={key}
            className="overflow-hidden cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => { setActiveTemp(null); filter ? loadLeads(filter) : fetchLeads(); }}
          >
            <div className={`h-1 ${color}`} />
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">{label}</p>
                  <p className={`text-3xl font-extrabold ${text}`}>{stats[key]}</p>
                </div>
                <div className="h-10 w-10 rounded-xl flex items-center justify-center bg-gray-50">
                  <Icon className={`h-5 w-5 ${text}`} />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* ── 2. Admin Analytics ──────────────────────────── */}
      {isAdmin && (
        <>
          {/* Follow-up Metrics */}
          {followupMetrics && (
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
              {[
                { label: "Today Followups",  value: followupMetrics.today,           color: "bg-blue-500",   text: "text-blue-600" },
                { label: "Missed Followups", value: followupMetrics.missed,          color: "bg-red-500",    text: "text-red-600" },
                { label: "Upcoming",         value: followupMetrics.pending,         color: "bg-yellow-500", text: "text-yellow-600" },
                { label: "Completed Today",  value: followupMetrics.completed_today, color: "bg-green-500",  text: "text-green-600" },
              ].map(({ label, value, color, text }) => (
                <Card key={label} className="overflow-hidden">
                  <div className={`h-1 ${color}`} />
                  <CardContent className="p-4">
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">{label}</p>
                    <p className={`text-3xl font-extrabold ${text}`}>{value ?? "—"}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Temperature Chart */}
          {temperatureData.length > 0 && (
            <Card>
              <CardContent className="p-6">
                <p className="text-base font-semibold text-gray-900 mb-5">Lead Temperature</p>
                <ResponsiveContainer width="100%" height={380}>
                  <PieChart>
                    <Pie
                      data={temperatureData}
                      dataKey="total"
                      nameKey="temperature"
                      outerRadius={140}
                      label={({ payload, percent }) =>
                        `${payload?.total} (${(percent * 100).toFixed(0)}%)`
                      }
                      labelLine={true}
                    >
                      {temperatureData.map((e, i) => (
                        <Cell key={i} fill={TEMP_COLORS[e.temperature?.toLowerCase()] || "#8884d8"} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(v, n) => [`${v} Leads`, n?.toUpperCase()]} />
                    <Legend
                      iconSize={14}
                      formatter={(v) => (
                        <span style={{ fontSize: 13 }}>{v?.toUpperCase()}</span>
                      )}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}

          {/* Course × Temperature Chart */}
          {courseData.length > 0 && (
            <Card>
              <CardContent className="p-6">
                <p className="text-base font-semibold text-gray-900 mb-5">Course × Temperature</p>
                <ResponsiveContainer width="100%" height={420}>
                  <BarChart data={courseData} layout="vertical" margin={{ top: 5, right: 40, left: 10, bottom: 5 }}>
                    <XAxis type="number" tick={{ fontSize: 13 }} />
                    <YAxis dataKey="course" type="category" width={200} tick={{ fontSize: 13 }} />
                    <Tooltip />
                    <Bar dataKey="hot"  stackId="a" fill="#22c55e" barSize={22}>
                      <LabelList dataKey="hot"  position="inside" fill="#fff" fontSize={12} />
                    </Bar>
                    <Bar dataKey="warm" stackId="a" fill="#eab308" barSize={22}>
                      <LabelList dataKey="warm" position="inside" fill="#000" fontSize={12} />
                    </Bar>
                    <Bar dataKey="cold" stackId="a" fill="#ef4444" barSize={22}>
                      <LabelList dataKey="cold" position="inside" fill="#fff" fontSize={12} />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}

          {/* Source Distribution Chart */}
          {sourceData.length > 0 && (
            <Card>
              <CardContent className="p-6">
                <p className="text-base font-semibold text-gray-900 mb-5">Lead Source Distribution</p>
                <ResponsiveContainer width="100%" height={420}>
                  <BarChart data={sourceData} margin={{ top: 15, right: 30, left: 20, bottom: 130 }}>
                    <XAxis
                      dataKey="source"
                      interval={0}
                      angle={-90}
                      textAnchor="end"
                      height={130}
                      tick={{ fontSize: 13 }}
                    />
                    <YAxis
                      tick={{ fontSize: 13 }}
                      label={{ value: "Leads", angle: -90, position: "insideLeft", fontSize: 13, offset: 10 }}
                    />
                    <Tooltip />
                    <Bar dataKey="total" radius={[8, 8, 0, 0]} barSize={40}>
                      <LabelList dataKey="total" position="top" fontSize={13} />
                      {sourceData.map((_, i) => (
                        <Cell key={i} fill={SOURCE_COLORS[i % SOURCE_COLORS.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}

          {/* Employee Performance Table */}
          {employeeMetrics.length > 0 && (
            <Card className="overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-100">
                <span className="text-sm font-semibold text-gray-900">Employee Performance</span>
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Rank</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Leads</TableHead>
                    <TableHead>Conversions</TableHead>
                    <TableHead>Rate</TableHead>
                    <TableHead>Incentive</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {employeeMetrics.map((e) => (
                    <TableRow key={e.assigned_counsellor}>
                      <TableCell className="font-bold text-gray-400">{e.rank}</TableCell>
                      <TableCell className="font-semibold text-gray-900">{e.full_name}</TableCell>
                      <TableCell>{e.leads_handled}</TableCell>
                      <TableCell>{e.conversions}</TableCell>
                      <TableCell>{e.conversion_rate}%</TableCell>
                      <TableCell className="font-bold text-green-600">
                        ₹ {(e.incentive || 0).toLocaleString()}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          )}
        </>
      )}

      {/* ── 3. Temperature Filters ──────────────────────── */}
      <div className="grid grid-cols-3 gap-4">
        {TEMP_FILTERS.map(({ key, emoji, label, badge }) => {
          const isActive = activeTemp === key;
          return (
            <Card
              key={key}
              className={`cursor-pointer transition-shadow ${isActive ? "ring-2 ring-offset-1 shadow-md ring-gray-400" : "hover:shadow-md"}`}
              onClick={() => {
                if (isActive) { setActiveTemp(null); fetchLeads(); }
                else { setActiveTemp(key); loadLeads(key); }
              }}
            >
              <CardContent className="p-4 text-center">
                <Badge variant={badge} className="text-sm px-4 py-1.5 font-semibold">
                  {emoji} {label}
                </Badge>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* ── 4. Search ───────────────────────────────────── */}
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
        <input
          type="text"
          placeholder="Search name / mobile / course"
          style={{ paddingLeft: "2.25rem" }}
          className="w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {/* ── 5. Leads Table (bottom) ─────────────────────── */}
      {filteredLeads.length > 0 && (
        <Card className="overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
            <span className="text-sm font-semibold text-gray-900">{title || "Leads"}</span>
            <span className="text-xs text-gray-400">{filteredLeads.length} records</span>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Mobile</TableHead>
                <TableHead>Course</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Temperature</TableHead>
                <TableHead>Next Followup</TableHead>
                {isAdmin && <TableHead>Assigned To</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredLeads.map((lead) => (
                <TableRow key={lead.id} className="cursor-pointer" onClick={() => onNavigate("crm-lead-profile", lead.id)}>
                  <TableCell className="font-semibold text-gray-900">{lead.student_name}</TableCell>
                  <TableCell className="text-gray-700">{lead.student_contact}</TableCell>
                  <TableCell className="text-gray-700 max-w-[160px]"><p className="truncate">{lead.interested_course}</p></TableCell>
                  <TableCell><Badge variant="pending">{lead.status}</Badge></TableCell>
                  <TableCell><Badge variant={lead.temperature || "secondary"}>{lead.temperature}</Badge></TableCell>
                  <TableCell className="text-gray-500 text-xs">{lead.next_followup || "—"}</TableCell>
                  {isAdmin && <TableCell className="text-gray-600 text-xs">{lead.assigned_to_name || "—"}</TableCell>}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
    </div>
  );
}
